import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Modal } from '@clarke-energia/foton';
import { zodResolver } from '@hookform/resolvers/zod';

import { DocumentItem, DocumentSection } from '@components/atoms/drive-upload-modal';

export type UploadPhase = 'idle' | 'uploading' | 'polling' | 'done' | 'error';

export interface IInvoiceUploadModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onFileChange: (file: File | File[]) => void;
  onUploadInvoice: () => void;
  phase: UploadPhase;
  errorMessage: string | null;
  hasAttachments?: boolean;
}

const DOCUMENT_TEXT = 'Envie a fatura em PDF ate 3MB';

const InvoiceDocumentsSchema = z.object({
  invoice: z.array(z.instanceof(File)).nullish(),
});

export type IInvoiceDocuments = z.infer<typeof InvoiceDocumentsSchema>;

const PHASE_CONFIG: Record<UploadPhase, { label: string; sublabel?: string }> = {
  idle: { label: 'Enviar fatura' },
  uploading: { label: 'Enviando...', sublabel: 'Enviando arquivo para processamento' },
  polling: { label: 'Processando...', sublabel: 'Extraindo dados da fatura. Isso pode levar ate 30 segundos.' },
  done: { label: 'Concluido!' },
  error: { label: 'Tentar novamente' },
};

export const InvoiceUploadModal = ({
  open,
  setOpen,
  onFileChange,
  onUploadInvoice,
  phase,
  errorMessage,
  hasAttachments = false,
}: IInvoiceUploadModalProps): JSX.Element => {
  const handleClose = () => {
    if (phase === 'uploading' || phase === 'polling') return;
    setOpen(false);
  };

  const formController = useForm({
    defaultValues: {},
    mode: 'all',
    resolver: zodResolver(InvoiceDocumentsSchema),
  });

  const isWorking = phase === 'uploading' || phase === 'polling';
  const config = PHASE_CONFIG[phase];

  return (
    <Modal className="md:w-auto px-6 !pb-0" open={open} setOpen={handleClose} title="Upload de Fatura">
      <FormProvider {...formController}>
        <form className="rounded-[10px] inline-flex flex-col justify-start items-center gap-6 w-full max-w-[70vw] h-fit">
          {/* File selector - hidden during processing */}
          {!isWorking && phase !== 'done' && (
            <div className="grid grid-cols-1 justify-start items-start gap-6 w-fit overflow-y-auto max-h-[40vh] pr-5">
              <DocumentSection title="Fatura">
                <DocumentItem name="invoice-file" title={DOCUMENT_TEXT} onFileChange={onFileChange} loading={false} />
              </DocumentSection>
            </div>
          )}

          {/* Processing state */}
          {isWorking && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              <div className="animate-spin">
                <svg className="w-10 h-10 text-[#00bf6a]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <p className="text-[16px] font-semibold text-[#222]">{config.label}</p>
              {config.sublabel && (
                <p className="text-[13px] text-[#818181] text-center max-w-[300px]">{config.sublabel}</p>
              )}
            </div>
          )}

          {/* Done state */}
          {phase === 'done' && (
            <div className="flex flex-col items-center gap-3 py-6 w-full">
              <div className="bg-green-100 rounded-full p-3">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-[16px] font-semibold text-green-700">Dados extraidos com sucesso!</p>
              <p className="text-[13px] text-[#818181]">Os campos foram preenchidos automaticamente.</p>
            </div>
          )}

          {/* Error state */}
          {phase === 'error' && errorMessage && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg w-full">
              <svg
                className="w-5 h-5 text-red-500 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <p className="text-[13px] text-red-700">{errorMessage}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="self-stretch inline-flex justify-start items-start gap-5">
            <Button
              type="button"
              kind="secondary"
              onClick={handleClose}
              disabled={isWorking}
              label={phase === 'done' ? 'Fechar' : 'Cancelar'}
              size="small"
              className="w-full"
            />
            {phase !== 'done' && (
              <Button
                type="button"
                kind="primary"
                size="small"
                onClick={onUploadInvoice}
                disabled={isWorking || (!hasAttachments && phase === 'idle')}
                loading={isWorking}
                label={config.label}
                className="w-full"
              />
            )}
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
